import React, { useState } from "react";
import {
  Grid,
  Button,
  Typography,
  Paper,
  Box,
  makeStyles,
  CircularProgress,
} from "@material-ui/core";
import { styled } from "@mui/material/styles";
import PublishIcon from "@material-ui/icons/Publish";
import GetAppIcon from "@material-ui/icons/GetApp";
import MUIDataTable from "mui-datatables";

// components
import PageTitle from "../../components/PageTitle";
import Widget from "../../components/Widget";

// service
import {
  downloadTransactionCorrectionTemplate,
  uploadTransactionCorrectionExcel,
} from "../../service/TransactionRequest";
import { ERROR_CODE_SUCCESS } from "../../components/common/constant/ResponseErrorCodeConstant";

const useStyles = makeStyles((theme) => ({
  uploadSection: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  buttonGroup: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  uploadButton: {
    position: "relative",
    overflow: "hidden",
  },
  fileInput: {
    position: "absolute",
    opacity: 0,
    width: "100%",
    height: "100%",
    cursor: "pointer",
  },
  fileName: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontStyle: "italic",
  },
  resultSection: {
    marginTop: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default function FixNewOrder() {
  const classes = useStyles();
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [resultData, setResultData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // 下载 Excel 模板
  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadTransactionCorrectionTemplate();
      
      // 创建 blob 链接并下载
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "transaction-correction-template.xlsx");
      // 添加到页面
      document.body.appendChild(link);
      // 触发下载
      link.click();
      // 清理并移除链接
      link.parentNode.removeChild(link);
      // 释放 URL 对象
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("下载模板失败:", error);
      alert("下载模板失败，请稍后重试");
    }
  };

  // 处理文件选择
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      setFileName(file.name);
      setUploadResult(null);
    }
  };

  // 上传文件
  const handleUpload = async () => {
    if (!selectedFile) {
      alert("请先选择要上传的文件");
      return;
    }

    setIsUploading(true);
    setUploadResult(null);
    setResultData([]);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await uploadTransactionCorrectionExcel(formData);
      
      console.log("上传响应:", response);

      if (response && response.code === ERROR_CODE_SUCCESS) {
        setUploadResult({
          success: true,
          message: response.message || "文件上传成功，处理完成",
        });
        
        // 如果响应中包含结果数据，直接显示
        if (response.data && Array.isArray(response.data)) {
          // 假设返回的数据格式为 [{orderId, status, message}, ...]
          const formattedData = response.data.map((item) => [
            item.orderId || item.id || "",
            item.status || "",
            item.message || item.msg || "",
          ]);
          setResultData(formattedData);
        } else if (response.data && response.data.records) {
          // 如果返回的是分页格式
          const formattedData = response.data.records.map((item) => [
            item.orderId || item.id || "",
            item.status || "",
            item.message || item.msg || "",
          ]);
          setResultData(formattedData);
        }
      } else {
        setUploadResult({
          success: false,
          message: response.message || "文件上传失败，请稍后重试",
        });
      }
    } catch (error) {
      console.error("上传失败:", error);
      setUploadResult({
        success: false,
        message: error.message || error.response?.data?.message || "文件上传失败，请稍后重试",
      });
    } finally {
      setIsUploading(false);
    }
  };

  // 表格列配置
  const columns = [
    {
      name: "orderId",
      label: "Order ID",
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          const color = value === "Success" ? "success" : "error";
          return (
            <Typography color={color} variant="body2">
              {value}
            </Typography>
          );
        },
      },
    },
    {
      name: "message",
      label: "Message",
      options: {
        filter: false,
        sort: false,
      },
    },
  ];

  // 表格选项
  const options = {
    filterType: "dropdown",
    responsive: "vertical",
    print: false,
    download: false,
    viewColumns: false,
    selectableRows: "none",
    rowsPerPage: 10,
    rowsPerPageOptions: [10, 25, 50],
  };

  return (
    <>
      <PageTitle title="Fix New Order" />
      <Grid container spacing={4}>
        {/* 操作区域 */}
        <Grid item xs={12}>
          <Widget title="Upload Excel File" disableWidgetMenu>
            <Paper className={classes.paper}>
              <Typography variant="h6" gutterBottom>
                操作步骤：
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                1. 点击"下载模板"按钮下载 Excel 模板文件
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                2. 按照模板格式填写订单信息
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                3. 选择填写好的 Excel 文件并上传
              </Typography>
              <Typography variant="body2" color="textSecondary">
                4. 查看处理结果
              </Typography>

              <Box className={classes.buttonGroup}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<GetAppIcon />}
                  onClick={handleDownloadTemplate}
                >
                  下载模板
                </Button>

                <Button
                  component="label"
                  variant="contained"
                  color="secondary"
                  startIcon={<PublishIcon />}
                  className={classes.uploadButton}
                  disabled={isUploading}
                >
                  选择文件
                  <VisuallyHiddenInput
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                  />
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpload}
                  disabled={!selectedFile || isUploading}
                >
                  {isUploading ? (
                    <>
                      <CircularProgress size={20} style={{ marginRight: 8 }} />
                      上传中...
                    </>
                  ) : (
                    "上传文件"
                  )}
                </Button>
              </Box>

              {fileName && (
                <Typography className={classes.fileName}>
                  已选择文件: {fileName}
                </Typography>
              )}

              {uploadResult && (
                <Box mt={2}>
                  <Paper
                    style={{
                      padding: 16,
                      backgroundColor: uploadResult.success
                        ? "#e8f5e9"
                        : "#ffebee",
                    }}
                  >
                    <Typography
                      variant="body2"
                      style={{
                        color: uploadResult.success ? "#2e7d32" : "#c62828",
                      }}
                    >
                      {uploadResult.message}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Paper>
          </Widget>
        </Grid>

        {/* 结果展示区域 */}
        {resultData.length > 0 && (
          <Grid item xs={12} className={classes.resultSection}>
            <Widget title="处理结果" disableWidgetMenu>
              <MUIDataTable
                title="Fix New Order Results"
                data={resultData}
                columns={columns}
                options={options}
              />
            </Widget>
          </Grid>
        )}

        {/* 空状态提示 */}
        {!isLoading && resultData.length === 0 && !uploadResult && (
          <Grid item xs={12}>
            <Widget title="处理结果" disableWidgetMenu>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={200}
              >
                <Typography variant="body1" color="textSecondary">
                  暂无处理结果，请先上传文件
                </Typography>
              </Box>
            </Widget>
          </Grid>
        )}
      </Grid>
    </>
  );
}
